import { useCallback, useEffect, useState } from 'react'
import './impersonationBanner.css'

/**
 * Persistent impersonation banner for the owner-facing app.
 *
 * Built in slice 3c as the counterpart to admin-side impersonation. It checks
 * the admin session endpoint (`/api/admin/auth/me`) — when an admin cookie is
 * present AND impersonating, that endpoint returns the impersonation context
 * even on the owner app, because the admin cookie is path-scoped to `/`.
 *
 * When the owner UI shell proper lands (slice 2b), this component should be
 * rendered at the top of that shell so it persists across every owner route.
 * For now App.tsx mounts it above the placeholder Home page.
 */

interface AdminMeShape {
  admin: { id: string; email: string; name: string }
  impersonating: {
    user_id: string
    tenant_id: string | null
    started_at: string
  } | null
}

export function ImpersonationBanner() {
  const [impersonation, setImpersonation] = useState<
    AdminMeShape['impersonating'] | null
  >(null)
  const [ending, setEnding] = useState(false)

  const check = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/auth/me', {
        credentials: 'include',
      })
      // NOTE: for a normal owner (no admin cookie) this returns 401 — that is
      // the EXPECTED "not impersonating" signal, not a bug. DevTools logs any
      // 4xx response in red, so you'll see a harmless `GET /api/admin/auth/me
      // 401` in the console on every owner page load (twice in dev — React
      // StrictMode double-invokes effects). The banner only renders when an
      // admin IS impersonating; the 401 path correctly hides it.
      if (!res.ok) {
        setImpersonation(null)
        return
      }
      const body = (await res.json()) as AdminMeShape
      setImpersonation(body.impersonating)
    } catch {
      // No admin session / network issue — nothing to show.
      setImpersonation(null)
    }
  }, [])

  useEffect(() => {
    void check()
  }, [check])

  async function endImpersonation() {
    if (!impersonation) return
    setEnding(true)
    const tenantId = impersonation.tenant_id
    try {
      const res = await fetch('/api/admin/impersonation/exit', {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('exit failed')
      // Redirect back to the tenant detail page where impersonation started.
      window.location.href = tenantId
        ? `/admin/tenants/${tenantId}`
        : '/admin/tenants'
    } catch {
      setEnding(false)
      // Surface inline — the owner app has no toast surface yet.
      alert('Could not end impersonation. Please try again.')
    }
  }

  if (!impersonation) return null

  return (
    <div className="owner-impersonation-banner" role="alert">
      <span>
        Impersonating tenant owner — all changes are audit-logged to your
        admin account.
      </span>
      <button
        type="button"
        onClick={() => void endImpersonation()}
        disabled={ending}
      >
        {ending ? 'Ending…' : 'End impersonation'}
      </button>
    </div>
  )
}
